import type { MetricsRow } from 'app/services/metrics'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { DateRangeOperator, getDateRangeValues } from 'app/filter.utils'
import { defaultColumns, detailColumns, filterableColumns } from 'app/table-columns/companies/locations'
import { useUser } from 'app/utils'
import { FilterableColumn } from 'app/filter.utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { graphql } from 'app/generated/gql'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { debug, logError } from 'lib/logging'
import moment from 'moment'

const LOCATION_MARKETING_METRICS_QUERY = graphql(`
  query LocationMarketingMetrics(
    $dateRange: DateRangeInput!
    $groupBy: String!
    $channel: String
    $showDisqualified: Boolean
  ) {
    marketingMetrics(dateRange: $dateRange, groupBy: $groupBy, channel: $channel, showDisqualified: $showDisqualified) {
      summary {
        budget
        facebookSpend
        googleSpend
        targetSpend
        leadGoal
        leads
        MQLs
        appointments
        patients
        production
        scheduledProduction
        ROI
        scheduleRate
        CPL
        CPQL
        CPA
        CAC
        disqualified
      }
      rows {
        bucket
        locationId
        leads
        MQLs
        appointments
        patients
        scheduleRate
        cpls
        roi
        budget
        facebookSpend
        googleSpend
        targetSpend
        leadGoal
        iCPL
        iCPQL
      }
    }
  }
`)

const LOCATION_TOTALS_QUERY = graphql(`
  query LocationTotals($dateRange: DateRangeInput!, $groupBy: String!, $channel: String, $locationIds: [String!]) {
    marketingMetrics(dateRange: $dateRange, groupBy: $groupBy, channel: $channel, locationIds: $locationIds) {
      rows {
        bucket
        leads
        MQLs
        cpls
        roi
        budget
        facebookSpend
        googleSpend
        targetSpend
        leadGoal
        iCPL
        iCPQL
      }
    }
  }
`)

export type UseCompaniesLocationsReturn = {
  filteredRows: MetricsRow[] | undefined
  columns: ColumnDef<MetricsRow, any>[]
  filterableColumns: FilterableColumn[]
  searchValue: string | undefined
  setSearchValue: (val: string | undefined) => void
  isDetailsOpen: boolean
  setIsDetailsOpen: (open: boolean) => void
  locationTotals: MetricsRow[] | undefined
  detailColumns: ColumnDef<MetricsRow, any>[]
  totalItems: number | undefined
  loadAllLocations: () => Promise<void>
  scrollHandler: React.UIEventHandler<HTMLDivElement>
}

type LocationHit = {
  id: string
  name: string
  orgTags?: string[]
  workspaceId?: string
  pmsIntegrated?: boolean
  lastPmsSyncDate?: string
  facebookAdsActive?: boolean
  googleAdsActive?: boolean
  googleAdsV3?: { accountId?: string } | null
}

const LOCATION_PAGE_SIZE = 1000
const LOCATION_LIST_FIELDS =
  'id name orgTags workspaceId pmsIntegrated lastPmsSyncDate facebookAdsActive googleAdsActive googleAdsV3 { accountId }'

export function useCompaniesLocations(): UseCompaniesLocationsReturn {
  const user = useUser()
  const [searchParams] = useSearchParams()
  const checkPermission = useCheckPermission()
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [locationTotals, setLocationTotals] = useState<MetricsRow[]>()
  const [searchValue, setSearchValue] = useState<string>()
  // Server-side aggregate over the ENTIRE dataset (not just loaded rows). Used for the table
  // footer totals so they stay stable while the user scrolls (TRK-5184).
  const [metricsSummary, setMetricsSummary] = useState<MetricsRow>()
  // The table lists ALL locations (lazy-loaded on scroll), with marketing metrics joined per
  // row. Rows are paged from the `locations` query so the count reflects the true total
  // (e.g. 2029) rather than the marketing-metrics aggregation, which the API caps at 1000.
  // Starts as `undefined` (not `[]`) so consumers can distinguish "not loaded yet" from
  // "loaded but empty" — this is what lets the table show a spinner on first load instead of
  // flashing the empty-state message before data arrives (TRK-5196).
  const [locationRows, setLocationRows] = useState<MetricsRow[] | undefined>(undefined)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [listLoading, setListLoading] = useState(false)
  // Marketing metrics keyed by locationId, fetched once per date range and joined onto each
  // location page. Locations without metrics this period render as empty/zero cells.
  const metricsByLocationRef = useRef<Map<string, MetricsRow>>(new Map())
  const savedDetailPatchesRef = useRef<Map<string, Partial<MetricsRow>>>(new Map())
  const workspaceNamesRef = useRef<Map<string, string>>(new Map())

  // Compute date range from search params
  const channel = isSMCOrg(user?.orgId) ? `Digital Marketing` : ``
  let fromDate = moment().startOf(`month`).format(`YYYY-MM-DD`)
  let toDate = moment().endOf(`month`).format(`YYYY-MM-DD`)
  let dateValue: string | string[] = searchParams.get(`date`) ?? `this_month`
  if (searchParams.has(`date`)) {
    const dateFilter: string[] = decodeURIComponent(searchParams.get(`date`)!).split(`,`)
    if (dateFilter.length > 1) {
      fromDate = dateFilter[0]
      toDate = dateFilter[1]
      dateValue = [fromDate, toDate]
    } else {
      const dates = getDateRangeValues(DateRangeOperator[dateFilter[0] as string])
      if (dates) {
        fromDate = dates[0].format(`YYYY-MM-DD`)
        toDate = dates[1].format(`YYYY-MM-DD`)
      }
    }
  }

  const patchDetailRow = useCallback(
    (row: MetricsRow, patch: Partial<MetricsRow>) => {
      setLocationTotals((rows) => rows?.map((item) => (item === row ? { ...item, ...patch } : item)))

      const month = row.bucket as string | undefined
      const locationId = row.locationId as string | undefined
      if (!month || !locationId) return

      const key = `${locationId}:${month}`
      const previousPatch = savedDetailPatchesRef.current.get(key) ?? {}
      const previous = { ...row, ...previousPatch }
      savedDetailPatchesRef.current.set(key, { ...previousPatch, ...patch })
      if (month < fromDate.slice(0, 7) || month > toDate.slice(0, 7)) return

      const deltas = Object.fromEntries(
        Object.entries(patch).map(([field, value]) => [field, Number(value ?? 0) - Number(previous[field] ?? 0)]),
      )
      const addDeltas = (metrics: MetricsRow): MetricsRow => ({
        ...metrics,
        ...Object.fromEntries(
          Object.entries(deltas).map(([field, delta]) => [field, Number(metrics[field] ?? 0) + Number(delta)]),
        ),
      })

      setLocationRows((rows) => rows?.map((item) => (item.locationId === locationId ? addDeltas(item) : item)))
      setMetricsSummary((summary) => (summary ? addDeltas(summary) : summary))
      const metrics = metricsByLocationRef.current.get(locationId)
      if (metrics) metricsByLocationRef.current.set(locationId, addDeltas(metrics))
    },
    [fromDate, toDate],
  )

  const getDetailRowPatch = useCallback((locationId: string, row: MetricsRow): Partial<MetricsRow> => {
    const month = row.bucket as string | undefined
    return month ? (savedDetailPatchesRef.current.get(`${locationId}:${month}`) ?? {}) : {}
  }, [])

  // Build a table row by joining a location with its marketing metrics (or zeros).
  const buildRow = (loc: LocationHit): MetricsRow => {
    const metrics = metricsByLocationRef.current.get(loc.id) ?? {}
    return {
      ...metrics,
      bucket: loc.id,
      locationId: loc.id,
      location: {
        name: loc.name,
        orgTags: loc.orgTags,
        pmsIntegrated: loc.pmsIntegrated,
        lastPmsSyncDate: loc.lastPmsSyncDate,
        facebookAdsActive: loc.facebookAdsActive,
        googleAdsActive: loc.googleAdsActive,
        googleAdsV3: loc.googleAdsV3,
      },
      workspace: { name: workspaceNamesRef.current.get(loc.workspaceId ?? ``) ?? `` },
    }
  }

  // Load one page of locations (server-side search) and append to the accumulated rows.
  const fetchLocationsPage = (pageToLoad: number) => {
    const params: {
      search?: string
      pagination: { page: number; limit: number }
      sort: { field: string; direction: string }
    } = {
      pagination: { page: pageToLoad, limit: LOCATION_PAGE_SIZE },
      sort: { field: `name.keyword`, direction: `asc` },
    }
    if (searchValue) params.search = `name:(*${searchValue}*)`
    return gqlFetchList<LocationHit>('locations', params, LOCATION_LIST_FIELDS)
  }

  const loadLocationsPage = (pageToLoad: number, replace: boolean) => {
    setListLoading(true)
    fetchLocationsPage(pageToLoad)
      .then((data) => {
        setTotal(data.total)
        const newRows = (data.hits ?? []).map(buildRow)
        setLocationRows((prev) => (replace ? newRows : [...(prev ?? []), ...newRows]))
      })
      .catch((err) => logError(`Error fetching locations`, err))
      .finally(() => setListLoading(false))
  }

  // Fetch marketing metrics + workspace names once per date range / filters. These are joined
  // onto each location row but do not depend on the search box.
  useEffect(() => {
    const dateRange = { fromDate, toDate }
    const vars: Record<string, unknown> = { dateRange, groupBy: 'locations', channel }
    // Match the legacy dashboard default: disqualified leads are hidden unless the URL opts in
    // with `showDisqualified=true`. Without this, lead/appointment counts include disqualified
    // leads and diverge from the legacy app (e.g. 11,500 vs 9,992).
    vars.showDisqualified = searchParams.get('showDisqualified') === 'true'
    Promise.all([
      gqlQuery<{ marketingMetrics: { rows: MetricsRow[]; summary?: MetricsRow } }>(
        LOCATION_MARKETING_METRICS_QUERY,
        vars,
      ),
      gqlFetchList<{ id: string; name: string }>('workspaces', {}, 'id name'),
    ])
      .then(([metricsResult, workspacesResult]) => {
        const metricsMap = new Map<string, MetricsRow>()
        for (const row of metricsResult.data?.marketingMetrics?.rows ?? []) {
          const id = row.locationId ?? row.bucket
          if (id) metricsMap.set(id, row)
        }
        metricsByLocationRef.current = metricsMap
        setMetricsSummary(metricsResult.data?.marketingMetrics?.summary)
        workspaceNamesRef.current = new Map(workspacesResult.hits.map((ws) => [ws.id, ws.name]))
        debug(`defer`, `have marketing data`, metricsMap.size)
        // Reload the first page so rows pick up the freshly loaded metrics/workspace joins.
        setPage(0)
        loadLocationsPage(0, true)
      })
      .catch((err) => logError(`Error fetching location metrics`, err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()])

  // Reload the first page of locations when the search term changes (server-side search so it
  // matches across all pages, not just the loaded ones). Skips the initial mount, where the
  // metrics effect above already triggers the first joined page load.
  const searchInitializedRef = useRef(false)
  useEffect(() => {
    if (!searchInitializedRef.current) {
      searchInitializedRef.current = true
      return
    }
    setPage(0)
    loadLocationsPage(0, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  const loadAllLocations = async (): Promise<void> => {
    if ((locationRows?.length ?? 0) >= total) return

    setListLoading(true)
    try {
      const pageCount = Math.ceil(total / LOCATION_PAGE_SIZE)
      const pages = await Promise.all(
        Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) => fetchLocationsPage(index + 1)),
      )
      setLocationRows((rows) => [
        ...(rows?.slice(0, LOCATION_PAGE_SIZE) ?? []),
        ...pages.flatMap((result) => (result.hits ?? []).map(buildRow)),
      ])
      setPage(Math.max(0, pageCount - 1))
    } catch (err) {
      logError(`Error fetching all locations for export`, err)
      throw err
    } finally {
      setListLoading(false)
    }
  }

  // Lazy-load subsequent pages as the user scrolls near the bottom.
  const haveMore = (locationRows?.length ?? 0) < total
  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event) => {
    const element = event.currentTarget
    if (haveMore && !listLoading && Math.abs(element.scrollHeight - element.clientHeight - element.scrollTop) < 800) {
      const next = page + 1
      setPage(next)
      loadLocationsPage(next, false)
    }
  }

  const showOrgAdminColumns = checkPermission([`org:marketing:see-spend`, `marketing:spend`])

  const safeFilterableColumns = useMemo(
    () =>
      filterableColumns(showOrgAdminColumns, {
        [`date`]: { value: dateValue, operator: `between` },
      }),
    [user, showOrgAdminColumns],
  )
  const safeDetailColumns = useMemo(
    () =>
      detailColumns(showOrgAdminColumns, isSMCOrg(user?.orgId), ``, (row, patch) => {
        setLocationTotals((rows) => rows?.map((item) => (item === row ? { ...item, ...patch } : item)))
      }),
    [user, showOrgAdminColumns],
  )
  const filteredRows = useClientSideTableFilters(
    locationRows,
    safeFilterableColumns,
    `bucket`,
    [`location.name`, `workspace.name`],
    // Search is handled server-side (see loadLocationsPage) so it can match locations across
    // all lazy-loaded pages, not just the ones currently rendered.
    undefined,
  )

  const openLocationsTotals = (locationIds: string[]) => {
    if (locationIds) {
      const channel = searchParams.get(`channel`) || `Digital Marketing`
      gqlQuery<{ marketingMetrics: { rows: MetricsRow[] } }>(LOCATION_TOTALS_QUERY, {
        dateRange: { fromDate, toDate },
        groupBy: 'months',
        channel,
        locationIds,
      })
        .then((result) => setLocationTotals(result.data?.marketingMetrics?.rows ?? []))
        .catch((err) => logError(`Error fetching location totals`, err))
    }
    setIsDetailsOpen(true)
  }

  const columns = useMemo(
    () =>
      defaultColumns(
        openLocationsTotals,
        showOrgAdminColumns,
        isSMCOrg(user?.orgId),
        fromDate ?? undefined,
        toDate ?? undefined,
        channel ?? `Digital Marketing`,
        metricsSummary,
        patchDetailRow,
        getDetailRowPatch,
      ),
    [searchParams, user, showOrgAdminColumns, metricsSummary, patchDetailRow, getDetailRowPatch],
  )

  return {
    filteredRows: filteredRows as MetricsRow[] | undefined,
    columns,
    filterableColumns: safeFilterableColumns,
    searchValue,
    setSearchValue,
    isDetailsOpen,
    setIsDetailsOpen,
    locationTotals,
    detailColumns: safeDetailColumns,
    // Server-side total of all locations in scope, so the header count is consistent with the
    // full lazy-loaded list rather than only the rows fetched so far.
    totalItems: total,
    loadAllLocations,
    scrollHandler,
  }
}
